import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, TrendingUp, TrendingDown, Minus, Activity, RefreshCw } from "lucide-react";

const TREND_ICON = {
  improving: { I: TrendingUp, cls: "text-emerald-600" },
  flat: { I: Minus, cls: "text-slate-500" },
  worsening: { I: TrendingDown, cls: "text-rose-600" },
};

export default function RecoveryProgress() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/scorecards/recovery-progress");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (<Card className="p-4 border-slate-200" data-testid="recovery-progress-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Loading recovery progress…</div></Card>);
  if (!data || data.forbidden || data.error) return null;

  const rows = data.managers || [];
  const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="recovery-progress">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-indigo-600"/>
          <h3 className="font-semibold text-slate-900 text-sm">Recovery Progress</h3>
          <span className="text-xs text-slate-500">{rows.length} coached</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="recovery-progress-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center" data-testid="recovery-progress-empty">No coaching emails sent yet. Send one from Scorecards or Recovery List.</div>
      ) : (
        <div className="space-y-1.5" data-testid="recovery-progress-list">
          {rows.slice(0, 5).map((m) => {
            const Ti = TREND_ICON[m.trend]?.I || Minus;
            const tcls = TREND_ICON[m.trend]?.cls || "text-slate-500";
            return (
              <div key={m.userId} className="flex items-center gap-3 p-2.5 rounded-md bg-slate-50 border border-slate-100" data-testid={`recovery-progress-row-${m.userId}`}>
                <Ti className={`h-4 w-4 ${tcls}`}/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 font-medium truncate">{m.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {m.gradeThen || "—"} ({m.scoreThen ?? "—"}) → <span className="font-semibold text-slate-800">{m.gradeNow || "—"} ({m.scoreNow})</span>
                    {m.daysSinceCoach != null && <span className="ml-1.5">· {m.daysSinceCoach}d ago</span>}
                    {m.postCoachActionsCompleted > 0 && <span className="ml-1.5 text-emerald-600">· {m.postCoachActionsCompleted} action(s)</span>}
                  </div>
                </div>
                <Badge className={`text-[10px] border px-1.5 py-0 ${m.trend === "improving" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : m.trend === "worsening" ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-slate-50 text-slate-700 border-slate-200"}`}>{m.trend}</Badge>
                {m.valueChangeGBP !== 0 && <div className="text-[11px] font-mono text-indigo-700">{m.valueChangeGBP > 0 ? "+" : ""}{fmtGBP(m.valueChangeGBP)}</div>}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
